import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Download, Filter, Calendar } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import MovimientoRow from '../../../components/Tesoreria/MovimientoRow';
import { usePagos } from '../../../graphql/tesoreria';

export default function ListadoPagos() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ fechaDesde: '', fechaHasta: '', estado: '', moneda: '' });
  const [page, setPage] = useState(1);

  const [result] = usePagos({ ...filters, page, pageSize: 10 });
  const pagos = result.data?.pagos?.data || [];

  const exportToCSV = () => {
    const headers = ['Fecha', 'Proveedor', 'Medio', 'Importe', 'Moneda', 'Estado'];
    const rows = pagos.map((p) => [
      p.fecha,
      p.proveedor?.nombre || '',
      p.medioPago?.nombre || '',
      p.importe,
      p.moneda,
      p.estado,
    ]);
    const csvContent = [headers.join(','), ...rows.map((r) => r.map((c) => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `pagos_${new Date().toISOString()}.csv`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-[rgba(var(--bg))]">
      <PageHeader
        title="Pagos a Proveedores"
        subtitle="Gestión de pagos y órdenes de pago"
        actions={[
          <button key="nuevo" onClick={() => navigate('/Tesoreria/Pagos/Nuevo')} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[rgb(var(--brand))] text-white hover:bg-[rgb(var(--brand-700))] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]">
            <Plus className="w-4 h-4" />
            Nuevo Pago
          </button>,
        ]}
      />

      <section className="max-w-[1400px] mx-auto px-4 py-6 grid grid-cols-12 gap-4">
        <div className="col-span-12 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)] overflow-hidden">
          <div className="p-4 border-b border-[rgb(var(--border))] bg-[rgba(var(--subpanel))]">
            <div className="flex flex-col gap-4">
              <div className="flex gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[rgb(var(--muted))]" />
                  <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar pagos..." className="w-full pl-10 pr-4 py-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]" />
                </div>
                <button onClick={exportToCSV} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[rgb(var(--border))] text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.3)] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]">
                  <Download className="w-4 h-4" />
                  Exportar
                </button>
              </div>
              <div className="flex gap-2">
                <input type="date" value={filters.fechaDesde} onChange={(e) => setFilters({ ...filters, fechaDesde: e.target.value })} className="px-3 py-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]" />
                <input type="date" value={filters.fechaHasta} onChange={(e) => setFilters({ ...filters, fechaHasta: e.target.value })} className="px-3 py-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]" />
                <select value={filters.estado} onChange={(e) => setFilters({ ...filters, estado: e.target.value })} className="px-3 py-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]">
                  <option value="">Todos los estados</option>
                  <option value="PENDIENTE">Pendiente</option>
                  <option value="PARCIAL">Parcial</option>
                  <option value="CERRADO">Cerrado</option>
                </select>
                <select value={filters.moneda} onChange={(e) => setFilters({ ...filters, moneda: e.target.value })} className="px-3 py-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]">
                  <option value="">Todas las monedas</option>
                  <option value="ARS">ARS</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[rgba(var(--subpanel))] border-b border-[rgb(var(--border))]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">Proveedor</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">Medio</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase">Importe</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">Moneda</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--border))]">
                {pagos.map((pago) => (
                  <MovimientoRow
                    key={pago.id}
                    movimiento={pago}
                    tipo="pago"
                    onView={(p) => navigate(`/Tesoreria/Pagos/${p.id}`)}
                    onEdit={(p) => navigate(`/Tesoreria/Pagos/${p.id}/Editar`)}
                    onDelete={(p) => console.log('Delete', p)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
